import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export type PlanCode = 'free' | 'growth' | 'global' | 'enterprise';
export type PaidTier = Exclude<PlanCode, 'free'>;
export type StripeInterval = 'monthly' | 'yearly';
export type SubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'canceled' | 'incomplete';

export interface Subscription {
  id: string;
  plan: PlanCode;
  status: SubscriptionStatus;
  interval: StripeInterval | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  gracePeriodEnd: string | null;
  seatCount: number;
  unitPriceCents: number | null;
  currency: string;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  updatedAt: string;
}

export interface Invoice {
  id: string;
  stripeInvoiceId: string | null;
  amountCents: number;
  currency: string;
  status: 'draft' | 'open' | 'paid' | 'void' | 'uncollectible';
  hostedInvoiceUrl: string | null;
  invoicePdfUrl: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  paidAt: string | null;
  createdAt: string;
}

export function useSubscription() {
  return useQuery({
    queryKey: ['billing-subscription'],
    queryFn: async () => {
      const r = await api.get<Subscription>('/api/billing/subscription');
      return r.data;
    },
  });
}

/** Canonical plan entry from GET /api/billing/plans (Stripe-sourced). */
export interface PlanCatalogEntry {
  code: PlanCode;
  unitPriceMonthlyCents: number;
  unitPriceYearlyCents: number;
  /** Lowercase ISO 4217 — `usd` today. */
  currency: string;
}

/**
 * Cached, Stripe-sourced plan catalogue. Use this everywhere prices
 * are rendered — never hardcode amounts in components or the picker
 * silently drifts from the Dashboard.
 */
export function usePlans() {
  return useQuery({
    queryKey: ['billing-plans'],
    queryFn: async () => {
      const r = await api.get<PlanCatalogEntry[]>('/api/billing/plans');
      return r.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useChangePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { plan: PlanCode; seatCount: number }) => {
      const r = await api.post<Subscription>('/api/billing/subscription/change-plan', args);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['billing-subscription'] }),
  });
}

export function useInvoices() {
  return useQuery({
    queryKey: ['billing-invoices'],
    queryFn: async () => {
      const r = await api.get<{ invoices: Invoice[] }>('/api/billing/invoices');
      return r.data.invoices;
    },
  });
}

export interface StripeRedirectResponse {
  url: string;
}

/**
 * Opens Stripe Customer Portal — POST /api/billing/portal returns
 * a session URL we redirect to. Customers manage plan / payment /
 * cancellation there. We never embed Stripe's UI inline.
 */
export function useStripePortal() {
  return useMutation({
    mutationFn: async (returnUrl?: string) => {
      const r = await api.post<StripeRedirectResponse>('/api/billing/portal', { returnUrl });
      return r.data;
    },
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });
}

export interface CheckoutInput {
  plan: PaidTier;
  interval: StripeInterval;
  seats: number;
  successUrl?: string;
  cancelUrl?: string;
}

/**
 * Starts (or upgrades to) a paid plan. POST /api/billing/checkout
 * returns a Stripe Checkout URL we redirect to; after payment, Stripe
 * fires checkout.session.completed → backend webhook flips the plan,
 * then the customer lands back on `successUrl`.
 */
export function useStartCheckout() {
  return useMutation({
    mutationFn: async (input: CheckoutInput) => {
      const r = await api.post<StripeRedirectResponse>('/api/billing/checkout', input);
      return r.data;
    },
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });
}
